import { AsyncSelect } from '@percent/lemonade';

import { OrganisationSelectProps } from './OrganisationSelect.types';
import { useMemo } from 'react';
import { useQuery } from '@percent/grants-nomination-form/hooks';
import { searchOrganisationsAction } from '@percent/grants-nomination-form/api/organisations/organisations.actions';
import { SelectOption } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';
import { useDebounce } from 'usehooks-ts';
import { OrganisationPreview } from './OrganisationPreview/OrganisationPreview';
import { useTranslation } from 'react-i18next';

const ORGANISATION_SEARCH_PAGE_SIZE = 10;
const MIN_SEARCH_LENGTH = 3;

export const OrganisationSelect = ({
  name,
  setOrganisationValue,
  setQueryValue,
  onChange,
  countryCode,
  placeholder,
  query = '',
  organisation: selectedOrganisation,
  addOrganisationEnabled,
  toggleAddOrganisation,
  allowedOrganisationsTypes,
}: OrganisationSelectProps) => {
  const { t } = useTranslation();
  const debouncedQuery = useDebounce(query, 500);

  const addOrganisationOption = { label: t('typography.addOption'), value: 'add-organisation' };

  const { data: searchData, isFetching } = useQuery(
    [
      'searchOrganisations',
      {
        query: debouncedQuery,
        countryCode,
        pageSize: ORGANISATION_SEARCH_PAGE_SIZE,
        type: allowedOrganisationsTypes,
      },
    ],
    searchOrganisationsAction,
    {
      enabled: !!countryCode && debouncedQuery.length >= MIN_SEARCH_LENGTH,
    },
  );

  const selectOptions: SelectOption[] = useMemo(
    () =>
      searchData?.data.map((org) => ({
        value: org.id,
        label: org.name,
        description: [org.address, org.postcode].filter(Boolean).join(', '),
      })) ?? [],
    [searchData],
  );

  const onOptionSelected = (organisation: SelectOption | null) => {
    onChange({ value: organisation?.value ?? '', label: organisation?.label ?? '' });
    setOrganisationValue(searchData?.data.find((org) => org.id === organisation?.value));
    toggleAddOrganisation(false);
  };

  const onAddOrganisation = () => {
    setOrganisationValue(undefined);
    onChange(addOrganisationOption);
    toggleAddOrganisation(true);
  };

  const getDefaultValue = () => {
    if (addOrganisationEnabled) {
      return addOrganisationOption;
    }

    return { value: selectedOrganisation?.id ?? '', label: selectedOrganisation?.name ?? '' };
  };

  return (
    <>
      <AsyncSelect
        name={name}
        key={countryCode}
        placeholder={placeholder}
        onChange={onOptionSelected}
        defaultValue={getDefaultValue()}
        disabled={!countryCode}
        options={selectOptions}
        setQuery={setQueryValue}
        query={selectedOrganisation?.name ?? query}
        loading={query.length > 0 && isFetching && !selectedOrganisation}
        loadingText={t('typography.searching')}
        noResultsFoundText={t(query ? 'typography.noResultsFound' : 'typography.pleaseEnterSearchTerm')}
        addOption={{
          label: t('typography.addOption'),
          onClick: onAddOrganisation,
          keepQuery: true,
          hideIcon: true,
        }}
        minSearchLength={MIN_SEARCH_LENGTH}
        minSearchLengthMessage={t('typography.minSearchLengthMessage', { count: MIN_SEARCH_LENGTH })}
        data-testid="organisationSearch"
      />
      {selectedOrganisation && <OrganisationPreview organisation={selectedOrganisation} />}
    </>
  );
};
